import * as StellarSdk from '@stellar/stellar-sdk';
import { Keypair, rpc, xdr } from '@stellar/stellar-sdk';

import { BASE_FEE } from 'constants/stellar';

import { getNetworkPassphrase } from 'helpers/env';
import { SorobanErrorHandler, SorobanPrepareTxErrorHandler } from 'helpers/error-handler';
import PromisedTimeout from 'helpers/promised-timeout';
import { getErrorMessage, getTxSentryExtra } from 'helpers/sentry';
import { retryTransactionSubmit } from 'helpers/transaction-retry';
import { getSorobanUrl } from 'helpers/url';

import { ModalService } from 'services/globalServices';
import SentryService from 'services/sentry.service';

import RestoreContractModal from 'web/modals/RestoreContractModal';

// Expected/transient submit failures we don't want to alert on in Sentry.
const NOISY_SUBMIT_ERROR_MESSAGES = new Set<string>([
    'Try again later',
    'Transaction failed because of timeout. Please make another attempt.',
]);

// Soroban RPC can return an account a ledger or two behind a transaction that has just landed;
// waitForAccountSequence polls it for about 10 seconds.
const ACCOUNT_SEQUENCE_ATTEMPTS = 10;
const ACCOUNT_SEQUENCE_RETRY_MS = 1000;

export default class Connection {
    private server: StellarSdk.rpc.Server | null = null;
    private keypair: Keypair | null = null;

    constructor() {
        this.startServer();
    }

    startServer(): void {
        this.server = new rpc.Server(getSorobanUrl());
    }

    loginWithSecret(secretKey: string): Promise<string> {
        return new Promise((resolve, reject) => {
            try {
                this.keypair = StellarSdk.Keypair.fromSecret(secretKey);

                resolve(this.keypair.publicKey());
            } catch (e) {
                reject(e);
            }
        });
    }

    logoutWithSecret() {
        if (this.keypair) {
            this.keypair = null;
        }
    }

    pollTx<T>(
        hash: string,
    ): Promise<
        T | rpc.Api.GetFailedTransactionResponse | rpc.Api.GetMissingTransactionResponse | xdr.ScVal
    > {
        return this.server
            .pollTransaction(hash, {
                attempts: 30,
                sleepStrategy: () => 2000,
            })
            .then(res => {
                if (res.status === 'SUCCESS') {
                    return res.returnValue;
                }
                throw new Error(
                    'Transaction failed because of timeout. Please make another attempt.',
                );
            });
    }

    processResponse(response: rpc.Api.SendTransactionResponse) {
        if (response.status === 'TRY_AGAIN_LATER') {
            throw new Error('Try again later');
        }
        if (response.status === 'DUPLICATE') {
            return this.pollTx(response.hash);
        }
        if (response.status !== 'PENDING') {
            throw new Error(SorobanErrorHandler(response.errorResult.result().switch().name));
        }
        return this.pollTx(response.hash);
    }

    async tryRestore(tx: StellarSdk.Transaction) {
        let sim: rpc.Api.SimulateTransactionResponse;

        try {
            sim = await this.server.simulateTransaction(tx);
        } catch (error) {
            SentryService.captureException({
                error,
                extra: getTxSentryExtra(tx, error),
                prefix: 'Soroban transaction simulation failed',
            });
            throw error;
        }

        // Simulation-error responses are intentionally passed through here —
        // server.prepareTransaction() further in the chain will surface a
        // user-friendly message via SorobanPrepareTxErrorHandler.

        if (!(sim as StellarSdk.rpc.Api.SimulateTransactionRestoreResponse).restorePreamble) {
            return Promise.resolve();
        }

        const account = await this.server.getAccount(tx.source);
        let fee = parseInt(BASE_FEE);

        fee += parseInt(
            (sim as StellarSdk.rpc.Api.SimulateTransactionRestoreResponse).restorePreamble
                .minResourceFee,
        );

        const restoreTx = new StellarSdk.TransactionBuilder(account, { fee: fee.toString() })
            .setNetworkPassphrase(getNetworkPassphrase())
            .setSorobanData(
                (
                    sim as StellarSdk.rpc.Api.SimulateTransactionRestoreResponse
                ).restorePreamble.transactionData.build(),
            )
            .addOperation(StellarSdk.Operation.restoreFootprint({}))
            .setTimeout(StellarSdk.TimeoutInfinite)
            .build();

        ModalService.openModal(RestoreContractModal, { tx: restoreTx });

        return Promise.reject({ message: 'Something expired' });
    }

    async buildSmartContractTx(
        publicKey: string,
        contractId: string,
        method: string,
        ...args: xdr.ScVal[]
    ) {
        const acc = await this.server.getAccount(publicKey);
        const contract = new StellarSdk.Contract(contractId);
        const builtTx = new StellarSdk.TransactionBuilder(acc, {
            fee: BASE_FEE,
            networkPassphrase: getNetworkPassphrase(),
        });
        if (args) {
            builtTx.addOperation(contract.call(method, ...args));
        } else {
            builtTx.addOperation(contract.call(method));
        }
        return builtTx.setTimeout(StellarSdk.TimeoutInfinite).build();
    }

    /**
     * Builds a transaction intended for simulation only (read-only contract
     * calls). The source account is constructed locally with a zero sequence
     * number: simulateTransaction doesn't validate the sequence, so this
     * avoids the extra getAccount RPC round-trip per read.
     * Never use the result for signing/submitting — the sequence is fake.
     */
    buildSmartContractTxForSimulate(
        publicKey: string,
        contractId: string,
        method: string,
        ...args: xdr.ScVal[]
    ): Promise<StellarSdk.Transaction> {
        const acc = new StellarSdk.Account(publicKey, '0');
        const contract = new StellarSdk.Contract(contractId);
        const builtTx = new StellarSdk.TransactionBuilder(acc, {
            fee: BASE_FEE,
            networkPassphrase: getNetworkPassphrase(),
        });
        if (args) {
            builtTx.addOperation(contract.call(method, ...args));
        } else {
            builtTx.addOperation(contract.call(method));
        }
        return Promise.resolve(builtTx.setTimeout(StellarSdk.TimeoutInfinite).build());
    }

    buildSmartContractTxFromOp(publicKey, operation) {
        return this.server.getAccount(publicKey).then(acc => {
            const builtTx = new StellarSdk.TransactionBuilder(acc, {
                fee: BASE_FEE,
                networkPassphrase: getNetworkPassphrase(),
            });

            builtTx.addOperation(operation);

            return builtTx.setTimeout(StellarSdk.TimeoutInfinite).build();
        });
    }

    signWithSecret(tx: StellarSdk.Transaction) {
        tx.sign(this.keypair);
        return tx;
    }

    getAccount(publicKey: string) {
        return this.server.getAccount(publicKey);
    }

    // Resolves once Soroban RPC returns the account at `sequence` or later. A transaction built
    // from RPC right after a transaction with that sequence landed would otherwise reuse the
    // sequence number and fail with tx_bad_seq after signing.
    async waitForAccountSequence(publicKey: string, sequence: string): Promise<void> {
        const target = BigInt(sequence);

        for (let attempt = 1; ; attempt += 1) {
            const account = await this.server.getAccount(publicKey);

            if (BigInt(account.sequenceNumber()) >= target) {
                return;
            }
            if (attempt >= ACCOUNT_SEQUENCE_ATTEMPTS) {
                throw new Error(
                    'The network has not caught up with your last transaction yet. Please try again in a moment.',
                );
            }
            await PromisedTimeout(ACCOUNT_SEQUENCE_RETRY_MS);
        }
    }

    async submitTx(tx: StellarSdk.Transaction) {
        let response: rpc.Api.SendTransactionResponse | undefined;

        try {
            return await retryTransactionSubmit(async () => {
                response = await this.server.sendTransaction(tx);
                return this.processResponse(response);
            });
        } catch (error) {
            // Skip retry/timeout cases — these are expected and would just create noise.
            if (!NOISY_SUBMIT_ERROR_MESSAGES.has(getErrorMessage(error) ?? '')) {
                SentryService.captureException({
                    error,
                    extra: {
                        ...getTxSentryExtra(tx, error),
                        responseHash: response?.hash,
                        responseStatus: response?.status,
                    },
                    prefix: 'Soroban transaction submit failed',
                });
            }

            throw error;
        }
    }

    async simulateTx(
        tx: StellarSdk.Transaction,
        // silent: don't report simulation errors to Sentry. Used by callers
        // that recover from expected failures themselves (e.g. batch reads
        // bisected on Budget/ExceededLimit).
        // expectedErrors: don't report simulation errors that contain one of
        // these markers, which the caller handles (e.g. a stale paging cursor
        // it retries); other failures are still reported.
        {
            silent = false,
            expectedErrors = [],
        }: { silent?: boolean; expectedErrors?: string[] } = {},
    ): Promise<StellarSdk.rpc.Api.SimulateTransactionSuccessResponse> {
        let response: rpc.Api.SimulateTransactionResponse;

        try {
            response = await this.server.simulateTransaction(tx);
        } catch (error) {
            if (!silent) {
                SentryService.captureException({
                    error,
                    extra: getTxSentryExtra(tx, error),
                    prefix: 'Soroban transaction simulation failed',
                });
            }
            throw error;
        }

        if (
            rpc.Api.isSimulationError(response) &&
            !silent &&
            !expectedErrors.some(marker => response.error.includes(marker))
        ) {
            SentryService.captureException({
                error: response.error,
                extra: {
                    ...getTxSentryExtra(tx, response.error),
                    latestLedger: response.latestLedger,
                },
                prefix: 'Soroban transaction simulation failed',
            });
        }

        return response as StellarSdk.rpc.Api.SimulateTransactionSuccessResponse;
    }

    prepareTransaction(tx: StellarSdk.Transaction) {
        return this.tryRestore(tx).then(() =>
            this.server.prepareTransaction(tx).catch(err => {
                SentryService.captureException({
                    error: err,
                    extra: getTxSentryExtra(tx, err),
                    prefix: 'Soroban transaction prepare failed',
                });
                throw SorobanPrepareTxErrorHandler(err);
            }),
        );
    }

    getLedgerEntries(...args: xdr.LedgerKey[]) {
        return this.server.getLedgerEntries(...args);
    }
}
